import Header from "@/components/Header";
import { Footer2 } from "@/components/ui/shadcnblocks-com-footer2";
import PageTransition from "@/components/PageTransition";
import { SEO } from "@/components/SEO";
import { useStaticTranslation } from "@/lib/staticTranslations";

const SHIPPING_FAQ_ITEMS = [
  {
    question: "כמה זמן לוקח משלוח עד הבית?",
    answer: "בישובים שאינם חריגים, זמני אספקה משוערים הם 2-5 ימי עסקים מיום ביצוע ההזמנה. ישובים חריגים עשויים להתעכב עד 12 ימי עסקים.",
  },
  {
    question: "מתי מתבצעות מסירות המשלוחים?",
    answer: "מסירת המשלוחים מתבצעת בימים א'-ה' בין השעות 08:00-19:00 וביום ו' בין השעות 08:00-12:00. חברת המשלוחים תיצור עמכם קשר לתיאום שעת המסירה.",
  },
  {
    question: "האם ניתן לאסוף את ההזמנה באופן עצמי?",
    answer: "כן, ניתן לאסוף הזמנות בסניף הראשי ברחוב הרצל 58, באר שבע, בשעות הפעילות. יש להגיע רק לאחר קבלת מייל המאשר שההזמנה מוכנה לאיסוף.",
  },
  {
    question: "מה קורה אם ההזמנה בוצעה אחרי השעה 15:00?",
    answer: "הזמנה שהתקבלה לאחר השעה 15:00 או בערב חג/יום שישי תטופל כהזמנה שהתקבלה ביום העסקים הבא.",
  },
  {
    question: "כמה זמן לוקח ייצור טבעות נישואין/אירוסין?",
    answer: "טבעות בהזמנה אישית כגון טבעות נישואין ואירוסין - זמן האספקה ישתנה בהתאם לזמן הייצור, לכל היותר עד 21 ימי עסקים.",
  },
];

const Shipping = () => {
  const { t, isHebrew } = useStaticTranslation("shipping");
  const { t: seoT } = useStaticTranslation("seo");

  return (
    <PageTransition>
      <SEO
        title={seoT("shippingTitle", "מדיניות משלוחים")}
        description={seoT("shippingDescription", "מדיניות משלוחים של אליס תכשיטים - משלוח עד הבית, איסוף עצמי, זמני אספקה ועלויות. משלוחים לכל רחבי הארץ")}
        canonical="https://alice.jewelry/shipping"
        breadcrumbs={[
          { name: seoT("home", "דף הבית"), url: '/' },
          { name: seoT("breadcrumbShipping", "מדיניות משלוחים"), url: '/shipping' },
        ]}
        faqItems={SHIPPING_FAQ_ITEMS}
      />
      <div className="min-h-screen flex flex-col" dir={isHebrew ? "rtl" : "ltr"}>
        <Header />
        
        <main className="flex-1">
          <section className="py-16 px-4">
            <div className="container mx-auto max-w-4xl">
              <div className="text-center mb-12 animate-fade-in">
                <div className="gold-divider mb-6" />
                <h1 className="text-4xl md:text-5xl font-serif mb-4">{t("title", "מדיניות משלוחים")}</h1>
              </div>

              <div className={`space-y-8 ${isHebrew ? 'text-right' : 'text-left'} animate-fade-in`} style={{ animationDelay: '0.1s' }}>
                <div>
                  <h2 className="text-2xl font-serif mb-4">{t("homeDelivery", "שליח עד הבית")}</h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p className="leading-relaxed">{t("homeDeliveryP1", "משלוחים לישובים חריגים עשויים להתעכב עד 12 ימי עסקים. ניתן למצוא את רשימת הישובים החריגים באתר דואר ישראל.")}</p>
                    <p className="leading-relaxed">{t("homeDeliveryP2", "לאחר קליטת המשלוח בחברת השליחויות ייצרו עמכם קשר במספר שהזנתם בעת ביצוע ההזמנה לתיאום מועד וטווח השעות המועדף עליכם לקבלת המשלוח.")}</p>
                    <p className="leading-relaxed">{t("homeDeliveryP3", "מסירת המשלוחים מתבצעת בימים א'-ה' בין השעות 08:00-19:00 וביום ו' בין השעות 08:00-12:00. חברת המשלוחים תיצור עמכם קשר על מנת לתאם את שעת מסירת החבילה.")}</p>
                    <p className="leading-relaxed font-semibold text-foreground">{t("homeDeliveryP4", "בישובים שאינם חריגים, זמני אספקה משוערים בד\"כ הינם 2-5 ימי עסקים מיום ביצוע ההזמנה באתר.")}</p>
                  </div>
                </div>

                <div className="pt-4">
                  <h2 className="text-2xl font-serif mb-4">{t("selfPickup", "איסוף עצמי")}</h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p className="leading-relaxed">{t("selfPickupP1", "הזמנות ניתן לאסוף בשלב זה רק בסניף הראשי, ברחוב הרצל 58, באר שבע, בשעות הפעילות.")}</p>
                    <p className="leading-relaxed font-semibold text-foreground">{t("selfPickupP2", "יש להגיע לאיסוף ההזמנה אך ורק לאחר קבלת מייל המאשר כי ההזמנה מוכנה לאיסוף.")}</p>
                    <p className="leading-relaxed">{t("selfPickupP3", "בנוסף ניתן לתאם איסוף מול שירות הלקוחות בטלפון 079-5575576 או במייל: info@alicejewelry.net")}</p>
                  </div>
                </div>

                <div className="bg-muted/30 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold mb-4">{t("additionalNotes", "הערות נוספות:")}</h3>
                  <ul className="space-y-3 text-muted-foreground text-sm">
                    <li>• {t("note1", "הזמנה שהתקבלה לאחר השעה 15:00 או בערב חג/יום שישי תטופל כהזמנה שהתקבלה ביום העסקים הבא.")}</li>
                    <li>• {t("note2", "הזמנות במידה מותאמת אישית, כגון, טבעות (נישואין/אירוסין) ובכלל, הזמנת טבעות נישואין: זמן האספקה ישתנה בהתאם לזמן הייצור של היצרן ואספקתו לחברה. לכל היותר עד 21 ימי עסקים.")}</li>
                    <li>• {t("note3", "במידה וייתכן עיכוב בהזמנה מכל סיבה שהיא אנו נדאג ליידע אתכן\\ם על כך.")}</li>
                  </ul>
                </div>

                {/* FAQ Section for AI Overviews */}
                <div className="pt-4">
                  <h2 className="text-2xl font-serif mb-6">שאלות נפוצות על משלוחים</h2>
                  <div className="space-y-6">
                    {SHIPPING_FAQ_ITEMS.map((item, index) => (
                      <div key={index} className="border-b border-border pb-4">
                        <h3 className="text-lg font-medium mb-2 text-foreground">{item.question}</h3>
                        <p className="text-muted-foreground leading-relaxed">{item.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer2 />
      </div>
    </PageTransition>
  );
};

export default Shipping;
